import { usePostAuthLoginMutation } from '@/services';
import { createContext, useContext, PropsWithChildren } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/app/store';
import { login, logout } from '@/ducks/auth-slice';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isSignupDisabled?: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: (returnTo?: string | null) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const dispatch = useDispatch();
  const [postLogin] = usePostAuthLoginMutation();
  const auth = useSelector((state: RootState) => state.auth);

  const handleLogin = async (email: string, password: string) => {
    try {
      const result = await postLogin({ body: { email, password } }).unwrap();
      dispatch(
        login({
          accessToken: result.data.accessToken,
          refreshToken: result.data.refreshToken,
        }),
      );
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const handleLogout = (returnTo?: string | null) => {
    dispatch(logout());
    if (returnTo) {
      window.location.href = returnTo;
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading: auth.isLoading ?? false,
        isAuthenticated: auth.loggedIn ?? false,
        isSignupDisabled: import.meta.env.VITE_DISABLE_SIGNUPS === 'true',
        login: handleLogin,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
